import { useState, useMemo } from "react";
import { ToggleButton } from "../components/buttons/toggle-button";
import { CloseButton } from "../components/buttons/close-button";
import { Button } from "../components/buttons/button";
import { Textarea } from "../components/inputs/textarea";

const MAX_FEEDBACK_LENGTH = 400;

const POSITIVE_OPTIONS = [
  { value: "factually-correct", label: "Factually correct" },
  { value: "easy-to-understand", label: "Easy to understand" },
  { value: "informative", label: "Informative" },
  { value: "creative-interesting", label: "Creative / Interesting" },
  { value: "other", label: "Other" },
];

const NEGATIVE_OPTIONS = [
  { value: "offensive-unsafe", label: "Offensive / Unsafe" },
  { value: "not-factually-correct", label: "Not factually correct" },
  { value: "didnt-follow-instructions", label: "Didn't follow instructions" },
  { value: "wrong-language", label: "Wrong language" },
  { value: "generic-bland", label: "Generic / Bland" },
  { value: "other", label: "Other" },
];

export interface FeedbackData {
  type: "positive" | "negative";
  feedbackText: string;
}

interface PropertyPalFeedbackWidgetProps {
  type: "positive" | "negative";
  onClose?: () => void;
  onSubmit?: (data: FeedbackData) => void;
}

function buildFeedbackText(
  selectedOptions: string[],
  options: { value: string; label: string }[],
  additionalFeedback: string,
): string {
  const selectedLabels = selectedOptions
    .map((value) => options.find((o) => o.value === value)?.label)
    .filter((label): label is string => Boolean(label))
    .map((label) => `[${label}]`);

  const prefix = selectedLabels.join("");
  const text = additionalFeedback.trim();

  if (prefix && text) return `${prefix} ${text}`;
  if (prefix) return prefix;
  return text;
}

export const PropertyPalFeedbackWidget = ({ type, onClose, onSubmit }: PropertyPalFeedbackWidgetProps) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [additionalFeedback, setAdditionalFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const options = type === "positive" ? POSITIVE_OPTIONS : NEGATIVE_OPTIONS;

  const feedbackText = useMemo(
    () => buildFeedbackText(selectedOptions, options, additionalFeedback),
    [selectedOptions, options, additionalFeedback],
  );

  const isOverLimit = feedbackText.length > MAX_FEEDBACK_LENGTH;

  const handleToggle = (value: string, selected: boolean) => {
    if (selected) {
      setSelectedOptions((prev) => [...prev, value]);
    } else {
      setSelectedOptions((prev) => prev.filter((v) => v !== value));
    }
  };

  const handleSubmit = () => {
    if (isOverLimit || isSubmitting) return;

    setIsSubmitting(true);
    onSubmit?.({
      type,
      feedbackText,
    });
  };

  return (
    <div className="bg-cloud border-solid border-t border-foggy-firth p-4 flex flex-col gap-3 rounded-b-lg">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-body-s font-medium text-slate">
          Help us improve <span className="font-normal text-light-slate">(optional)</span>
        </span>
        <CloseButton onClick={onClose} />
      </div>

      {/* Toggle Options */}
      <div
        role="group"
        aria-label={`Select what made this response ${type}`}
        className="flex flex-wrap gap-x-2.5 gap-y-1.5"
      >
        {options.map((option) => (
          <ToggleButton
            key={option.value}
            value={option.value}
            label={option.label}
            selected={selectedOptions.includes(option.value)}
            onToggle={handleToggle}
          />
        ))}
      </div>

      {/* Textarea */}
      <Textarea
        state={isOverLimit ? "error" : "default"}
        placeholder="Provide additional feedback"
        aria-label="Additional feedback"
        value={additionalFeedback}
        onChange={(e) => setAdditionalFeedback(e.target.value)}
        message={isOverLimit ? `Feedback cannot exceed ${String(MAX_FEEDBACK_LENGTH)} characters` : undefined}
        className="h-[84px] text-small-body-md md:text-small-body-md"
      />

      {/* Submit Button */}
      <Button
        label="Submit"
        colorVariant="secondary"
        variant="solid"
        onClick={handleSubmit}
        disabled={isOverLimit || isSubmitting}
        loading={isSubmitting}
        loadingLabel="Submitting..."
        className="w-full justify-center"
      />

      {/* Disclaimer */}
      <p className="text-body-xs text-light-slate leading-snug">
        We may attempt to use your feedback to improve the responses provided by ESPC&#39;s My Property Pal
      </p>
    </div>
  );
};
